<template>
  <van-popup
    :value="value"
    @input="updateValue"
    round
    closeable
    :position="isMobile ? 'bottom' : 'center'"
    :style="popupStyle"
    :get-container="isMobile ? 'body' : undefined"
    :lock-scroll="false"
    :overlay-style="isMobile ? { zIndex: 2000 } : {}"
  >
    <div class="airwallex-popup-content">
      <div class="popup-header">
        <!-- <div class="header-indicator"></div> -->
        <h3 class="header-title">Card</h3>
      </div>

      <div class="form-content">
        <label class="form-field">
          <span class="field-label">Card number</span>
          <div id="cardNumber" class="field-input"></div>
        </label>

        <div class="form-row">
          <label class="form-field half">
            <span class="field-label">Expiration date</span>
            <div id="expiry" class="field-input"></div>
          </label>

          <label class="form-field half">
            <span class="field-label">Security code</span>
            <div id="cvc" class="field-input"></div>
          </label>
        </div>
      </div>

      <div class="payBtnBox">
        <button
          class="payBtn"
          :class="{ loading: isLoading, disabled: !isFormValid }"
          :disabled="!isFormValid || isLoading"
          @click="handleSubmit"
        >
          <span v-if="isLoading" class="loading-spinner"></span>
          {{ $t("common.payConfirm") }}
        </button>
      </div>
    </div>
  </van-popup>
</template>

<script>
import { Toast } from "vant";

export default {
  name: "AirwallexPayment",
  props: {
    value: {
      type: Boolean,
      default: false
    },
    clientSecret: {
      type: String,
      default: ""
    },
    amount: {
      type: [String, Number],
      default: 0
    },
    currency: {
      type: String,
      default: "USD"
    },
    environment: {
      type: String,
      default: "demo" // demo, staging, prod
    },
    isMobile: {
      type: Boolean,
      default: false
    },
    responseData: {
      type: Object,
      default: () => ({})
    }
  },

  data() {
    return {
      cardNumber: null,
      expiry: null,
      cvc: null,
      isLoading: false,
      isFormValid: false,
      isInitialized: false,
      // 跟踪每个字段的验证状态
      cardNumberValid: false,
      expiryValid: false,
      cvcValid: false
    };
  },

  computed: {
    popupStyle() {
      if (this.isMobile) {
        return {
          height: "60%"
        };
      } else {
        // PC端居中弹窗
        return {
          width: "480px",
          maxHeight: "600px",
          borderRadius: "12px"
        };
      }
    },

    // 从 responseData 中提取所需的数据
    computedClientSecret() {
      if (
        this.responseData &&
        this.responseData.data &&
        this.responseData.data.data
      ) {
        try {
          const data = JSON.parse(this.responseData.data.data.url);
          return JSON.parse(data.paymentIntent).client_secret;
        } catch (error) {
          console.error(
            "Error parsing client secret from responseData:",
            error
          );
        }
      }
      return this.clientSecret;
    },

    computedPayTradeNo() {
      if (
        this.responseData &&
        this.responseData.data &&
        this.responseData.data.data
      ) {
        return this.responseData.data.data.payTradeNo;
      }
      return "";
    },

    computedCountryCode() {
      if (
        this.responseData &&
        this.responseData.data &&
        this.responseData.data.data
      ) {
        try {
          const data = JSON.parse(this.responseData.data.data.url);
          return data.countryCode;
        } catch (error) {
          console.error("Error parsing country code from responseData:", error);
        }
      }
      return "";
    },

    computedAmount() {
      if (
        this.responseData &&
        this.responseData.data &&
        this.responseData.data.data
      ) {
        try {
          const data = JSON.parse(this.responseData.data.data.url);
          return data.amount || this.amount;
        } catch (error) {
          console.error("Error parsing amount from responseData:", error);
        }
      }
      return this.amount;
    },

    computedCurrency() {
      if (
        this.responseData &&
        this.responseData.data &&
        this.responseData.data.data
      ) {
        try {
          const data = JSON.parse(this.responseData.data.data.url);
          return data.currency || this.currency;
        } catch (error) {
          console.error("Error parsing currency from responseData:", error);
        }
      }
      return this.currency;
    },

    // 从 responseData 中解析出 payment intent id
    computedPaymentIntentId() {
      if (
        this.responseData &&
        this.responseData.data &&
        this.responseData.data.data
      ) {
        try {
          const data = JSON.parse(this.responseData.data.data.url);
          const paymentIntentData = JSON.parse(data.paymentIntent);
          return paymentIntentData.id;
        } catch (error) {
          console.error(
            "Error parsing payment intent id from responseData:",
            error
          );
        }
      }
      return "";
    }
  },

  watch: {
    value(newVal) {
      console.log("AirwallexPayment value changed:", newVal);
      if (newVal && !this.isInitialized) {
        // 移动端需要更长的延迟来确保DOM完全渲染
        const delay = this.isMobile ? 300 : 0;
        setTimeout(() => {
          this.$nextTick(() => {
            this.initializeAirwallex();
          });
        }, delay);
      }
    },

    responseData: {
      handler(newVal) {
        console.log("AirwallexPayment responseData changed:", newVal);
        console.log("Computed client secret:", this.computedClientSecret);
        console.log("Computed pay trade no:", this.computedPayTradeNo);
        console.log("Computed country code:", this.computedCountryCode);
        console.log(
          "Computed payment intent id:",
          this.computedPaymentIntentId
        );
      },
      deep: true,
      immediate: true
    }
  },

  methods: {
    updateValue(val) {
      this.$emit("input", val);
    },

    // 上报日志方法
    reportLog(params) {
      this.$emit("report-log", params);
      console.log("Airwallex Payment Log:", params);
    },

    async initializeAirwallex() {
      console.log(
        "initializeAirwallex called, isInitialized:",
        this.isInitialized,
        "window.Airwallex:",
        !!window.Airwallex
      );
      if (this.isInitialized) {
        return;
      }

      // 如果 Airwallex 还没有加载，等待一段时间并重试
      if (!window.Airwallex) {
        console.log("Airwallex not loaded yet, waiting...");
        let retries = 0;
        const maxRetries = 20; // 最多重试20次
        const retryInterval = 200; // 每次重试间隔200ms

        while (!window.Airwallex && retries < maxRetries) {
          await new Promise(resolve => setTimeout(resolve, retryInterval));
          retries++;
          console.log(
            `Waiting for Airwallex... attempt ${retries}/${maxRetries}`
          );
        }

        if (!window.Airwallex) {
          console.error(
            "Airwallex script failed to load after maximum retries"
          );
          Toast.fail(
            this.$t("airwallex.scriptLoadError") ||
              "Airwallex脚本加载失败，请刷新页面重试"
          );
          this.$emit("error", new Error("Airwallex script load timeout"));
          return;
        }
      }

      try {
        console.log(
          "Initializing Airwallex with environment:",
          this.environment
        );
        // 初始化 Airwallex
        await window.Airwallex.init({
          env: this.environment,
          origin: window.location.origin
        });

        // 创建表单元素
        this.createPaymentElements();
        this.isInitialized = true;

        console.log("Airwallex initialization completed");
        this.$emit("initialized");
      } catch (error) {
        console.error("Airwallex initialization failed:", error);
        Toast.fail(this.$t("airwallex.initError") || "初始化失败，请重试");
        this.$emit("error", error);
      }
    },

    createPaymentElements() {
      try {
        // 重置验证状态
        this.cardNumberValid = false;
        this.expiryValid = false;
        this.cvcValid = false;
        this.isFormValid = false;

        // 检查DOM元素是否存在
        const cardNumberEl = document.getElementById("cardNumber");
        const expiryEl = document.getElementById("expiry");
        const cvcEl = document.getElementById("cvc");

        if (!cardNumberEl || !expiryEl || !cvcEl) {
          console.error("Airwallex mount elements not found");
          setTimeout(() => this.createPaymentElements(), 100);
          return;
        }

        console.log("Creating Airwallex elements...");

        // 移动端特殊配置
        const elementOptions = this.isMobile
          ? {
              style: {
                base: {
                  fontSize: "16px", // 避免iOS缩放
                  lineHeight: "20px"
                }
              }
            }
          : {};

        // 创建卡号输入框
        this.cardNumber = window.Airwallex.createElement(
          "cardNumber",
          elementOptions
        );
        this.cardNumber.mount("cardNumber");

        // 创建有效期输入框
        this.expiry = window.Airwallex.createElement("expiry", elementOptions);
        this.expiry.mount("expiry");

        // 创建CVC输入框
        this.cvc = window.Airwallex.createElement("cvc", elementOptions);
        this.cvc.mount("cvc");

        console.log("Airwallex elements created successfully");

        // 监听表单验证状态
        this.setupFormValidation();
      } catch (error) {
        console.error("Error creating Airwallex elements:", error);
        Toast.fail(this.$t("airwallex.elementError") || "表单创建失败");
      }
    },

    setupFormValidation() {
      // 监听卡号验证状态
      if (this.cardNumber) {
        this.cardNumber.on("change", event => {
          console.log("Card number change event:", event);
          this.cardNumberValid = event.detail.complete && !event.detail.error;
          this.validateForm();
        });
      }

      // 监听有效期验证状态
      if (this.expiry) {
        this.expiry.on("change", event => {
          console.log("Expiry change event:", event);
          this.expiryValid = event.detail.complete && !event.detail.error;
          this.validateForm();
        });
      }

      // 监听CVC验证状态
      if (this.cvc) {
        this.cvc.on("change", event => {
          console.log("CVC change event:", event);
          this.cvcValid = event.detail.complete && !event.detail.error;
          this.validateForm();
        });
      }
    },

    validateForm() {
      // 只有当所有字段都有效时，表单才有效
      this.isFormValid =
        this.cardNumberValid && this.expiryValid && this.cvcValid;
      console.log("Form validation status:", {
        cardNumberValid: this.cardNumberValid,
        expiryValid: this.expiryValid,
        cvcValid: this.cvcValid,
        isFormValid: this.isFormValid
      });
    },

    async handleSubmit() {
      if (!this.isFormValid || this.isLoading) {
        return;
      }

      this.isLoading = true;

      try {
        if (!this.computedClientSecret) {
          throw new Error("Client secret is required");
        }

        // 确认支付意图
        const result = await window.Airwallex.confirmPaymentIntent({
          element: this.cardNumber,
          id: this.computedPaymentIntentId, // 优先使用解析出的 id，否则从 client_secret 中提取
          client_secret: this.computedClientSecret
        });
        console.log("Payment result:", result);
        this.$emit("payment-success", result);
        // 上报日志

        this.reportLog({
          eventId: "PAYMENT_SUCCESS",
          msg: result
        });

        // 显示成功消息
        // const successMsg = this.$t('paymentSuccess') || '支付成功';
        // Toast.success(successMsg);

        // 关闭弹窗
        this.updateValue(false);
      } catch (error) {
        console.error("Payment failed:", error);
        this.$emit("payment-error", error);

        // 上报日志
        this.reportLog({
          eventId: "PAYMENT_ERROR",
          msg: {
            error: error.message || error.toString(),
            code: error.code || "UNKNOWN_ERROR"
          }
        });

        const errorMsg =
          error.message || this.$t("payError") || "支付失败，请重试";
        Toast.fail(errorMsg);

        // 支付失败后关闭弹窗
        this.updateValue(false);
      } finally {
        this.isLoading = false;
      }
    },

    // 清理资源
    cleanup() {
      if (this.cardNumber) {
        this.cardNumber.unmount();
        this.cardNumber = null;
      }
      if (this.expiry) {
        this.expiry.unmount();
        this.expiry = null;
      }
      if (this.cvc) {
        this.cvc.unmount();
        this.cvc = null;
      }
      // 重置所有状态
      this.isInitialized = false;
      this.cardNumberValid = false;
      this.expiryValid = false;
      this.cvcValid = false;
      this.isFormValid = false;
    }
  },

  beforeDestroy() {
    this.cleanup();
  }
};
</script>

<style lang="less" scoped>
.airwallex-popup-content {
  padding: 0;
  background: #fff;
  border-radius: 16px;
  overflow: hidden;
  position: relative;
  z-index: 2001;

  .popup-header {
    position: relative;
    padding: 16px 24px 24px;
    text-align: center;
    border-bottom: 1px solid #f0f0f0;

    .header-indicator {
      width: 40px;
      height: 4px;
      background: #e1e5e9;
      border-radius: 2px;
      margin: 0 auto 16px;
    }

    .header-title {
      font-size: 18px;
      font-weight: 600;
      color: #333;
      margin: 0;
    }
  }

  .form-content {
    padding: 24px;
  }

  .form-field {
    display: block;
    margin-bottom: 20px;

    &.half {
      width: calc(50% - 8px);
      display: inline-block;

      &:first-child {
        margin-right: 16px;
      }
    }

    .field-label {
      display: block;
      font-size: 14px;
      color: #666;
      font-weight: 500;
      margin-bottom: 8px;
    }

    .field-input {
      border: 1px solid #e1e5e9;
      border-radius: 8px;
      padding: 12px 16px;
      min-height: 20px;
      background: #fff;
      transition: all 0.3s ease;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
      position: relative;
      z-index: 1;

      // 确保移动端能正常输入
      -webkit-user-select: text;
      -moz-user-select: text;
      -ms-user-select: text;
      user-select: text;

      // 移动端触摸优化
      -webkit-tap-highlight-color: transparent;
      -webkit-touch-callout: none;

      &:focus-within {
        border-color: #ffaa0a;
        box-shadow: 0 0 0 3px rgba(255, 170, 10, 0.1);
      }

      &:hover {
        border-color: #c5c9cc;
      }
    }
  }

  .form-row {
    display: flex;
    gap: 16px;

    .form-field {
      flex: 1;
      margin-bottom: 20px;
    }
  }

  .payBtnBox {
    padding: 0 24px 24px;

    .payBtn {
      width: 100%;
      height: 48px;
      background: linear-gradient(135deg, #ffaa0a 0%, #ff9500 100%);
      border-radius: 8px;
      font-size: 16px;
      font-weight: 600;
      color: #fff;
      border: none;
      cursor: pointer;
      transition: all 0.3s ease;
      box-shadow: 0 4px 12px rgba(255, 170, 10, 0.3);
      letter-spacing: 0.5px;
      display: flex;
      align-items: center;
      justify-content: center;
      position: relative;

      &:hover:not(.disabled) {
        background: linear-gradient(135deg, #ec9d0a 0%, #e68900 100%);
        transform: translateY(-1px);
        box-shadow: 0 6px 16px rgba(255, 170, 10, 0.4);
      }

      &:active:not(.disabled) {
        transform: translateY(0);
        box-shadow: 0 2px 8px rgba(255, 170, 10, 0.3);
      }

      &.disabled,
      &:disabled {
        background: #ccc;
        cursor: not-allowed;
        transform: none;
        box-shadow: none;
      }

      &.loading {
        cursor: not-allowed;

        .loading-spinner {
          width: 16px;
          height: 16px;
          border: 2px solid #fff;
          border-top: 2px solid transparent;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin-right: 8px;
        }
      }
    }
  }
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

/* 移动端适配 */
@media (max-width: 768px) {
  .airwallex-popup-content {
    // 移动端只有顶部圆角
    border-radius: 16px 16px 0 0 !important;
    // 确保在移动端有正确的层级
    position: relative;
    z-index: 2002;

    .popup-header {
      padding: 12px 20px 20px;

      .header-title {
        font-size: 16px;
      }
    }

    .form-content {
      padding: 20px;
    }

    .form-field {
      margin-bottom: 16px;

      .field-label {
        font-size: 13px;
      }

      .field-input {
        padding: 12px 14px;
        min-height: 24px;

        // 移动端输入框优化
        font-size: 16px !important; // 防止iOS缩放
        -webkit-appearance: none;
        -webkit-border-radius: 8px;
        border-radius: 8px;

        // 确保能接收触摸事件
        pointer-events: auto;
        touch-action: manipulation;

        // Airwallex 元素特殊处理
        :global(.airwallex-payment-element) {
          font-size: 16px !important;
          line-height: 24px !important;
        }

        :global(input) {
          font-size: 16px !important;
          -webkit-appearance: none;
          border: none;
          outline: none;
          background: transparent;
        }
      }
    }

    .form-row {
      gap: 12px;
    }

    .payBtnBox {
      padding: 0 20px 20px;

      .payBtn {
        height: 44px;
        font-size: 15px;
        // 确保按钮能正常点击
        touch-action: manipulation;
        -webkit-tap-highlight-color: transparent;
      }
    }
  }
}
</style>
